import { api } from '../../lib/api';

/**
 * The system map surface: connecting GitHub, choosing repositories, and the
 * map built from them.
 *
 * Everything here is `workspace.admin` on the server. Reading a client's
 * source is the most sensitive capability this product has.
 */

export interface ConnectionStatus {
  /** False when this deployment has no GitHub App registered. */
  readonly appConfigured: boolean;
  readonly connected: boolean;
  readonly accountLogin: string | null;
  readonly repositorySelection: 'selected' | 'all' | null;
  readonly tokenFallbackConfigured: boolean;
  readonly setupSteps: readonly SetupStep[];
  readonly tokenCaveat: string;
}

/** One step of a setup guide — a heading, optional link, and the values to enter. */
export interface SetupStep {
  readonly title: string;
  readonly why?: string;
  readonly open?: { readonly label: string; readonly url: string };
  readonly fields?: ReadonlyArray<{
    readonly label: string;
    readonly value: string;
    readonly copy?: boolean;
    /** true draws a ticked box, false an unticked one — "leave this off" is an instruction. */
    readonly set?: boolean;
  }>;
  readonly warn?: string;
}

export interface AvailableRepo {
  readonly fullName: string;
  readonly name: string;
  readonly htmlUrl: string;
  readonly defaultBranch: string;
  readonly private: boolean;
  readonly mapped: boolean;
}

export interface MappedRepo {
  readonly id: string;
  readonly name: string;
  readonly url: string;
  readonly defaultBranch: string;
}

export interface SystemMapArea {
  readonly name: string;
  readonly summary: string;
  readonly aliases: readonly string[];
  readonly paths: readonly string[];
  readonly frontendRoutes: readonly string[];
  readonly backendEndpoints: readonly string[];
  readonly entitiesRead: readonly string[];
  readonly entitiesWritten: readonly string[];
  readonly sharesDataWith: readonly string[];
  readonly integrations: readonly string[];
  readonly platforms: readonly string[];
  readonly changeCost: 'low' | 'medium' | 'high';
  readonly notes: string;
}

export interface SystemMapContent {
  readonly areas: readonly SystemMapArea[];
  readonly glossary: ReadonlyArray<{ readonly term: string; readonly means: string; readonly area: string }>;
  readonly stack: readonly string[];
  readonly platformNote: string;
}

export interface SystemMapRow {
  readonly version: number;
  readonly content: SystemMapContent;
  readonly commitShas: Record<string, string>;
  readonly builtAt: string;
  readonly reviewedAt: string | null;
}

function base(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/system-map`;
}

export const systemMapKeys = {
  map: (workspaceId: string) => ['ws', workspaceId, 'system-map'] as const,
  connection: (workspaceId: string) => ['ws', workspaceId, 'system-map', 'connection'] as const,
  repos: (workspaceId: string) => ['ws', workspaceId, 'system-map', 'repos'] as const,
  branches: (workspaceId: string, repoId: string) =>
    ['ws', workspaceId, 'system-map', 'branches', repoId] as const,
  branchesForUrl: (workspaceId: string, url: string) =>
    ['ws', workspaceId, 'system-map', 'branches-for-url', url] as const,
  available: (workspaceId: string) => ['ws', workspaceId, 'system-map', 'available'] as const,
};

/**
 * The map, plus whether a build is outstanding.
 *
 * `build` is server truth rather than something the screen remembers, so a
 * build stays visible across a navigation, a reload, or a different person
 * opening the page.
 */
export interface BuildInfo {
  readonly state: 'queued' | 'running' | null;
  readonly lastAttemptAt: string | null;
  /** True when the last attempt failed — which "no map yet" alone would hide. */
  readonly lastFailed: boolean;
  /**
   * What the running build is doing now. Null when nothing is running, and
   * also when the API restarted mid-build — `state` is still the authority,
   * so the screen falls back to "Building…" rather than losing the build.
   */
  readonly progress: {
    readonly phase: 'reading' | 'sampling' | 'thinking' | 'saving';
    readonly detail: string;
    readonly at: string;
  } | null;
}

export function fetchMap(workspaceId: string): Promise<{ map: SystemMapRow | null; build: BuildInfo }> {
  return api.get(base(workspaceId));
}

export function fetchConnection(workspaceId: string): Promise<ConnectionStatus> {
  return api.get(`${base(workspaceId)}/connection`);
}

export function fetchMappedRepos(workspaceId: string): Promise<{ repos: MappedRepo[] }> {
  return api.get(`${base(workspaceId)}/repos`);
}

/** What GitHub granted. Only meaningful once connected. */
export function fetchAvailableRepos(workspaceId: string): Promise<{ repos: AvailableRepo[] }> {
  return api.get(`${base(workspaceId)}/repos/available`);
}

export function startInstall(workspaceId: string): Promise<{ url: string }> {
  return api.post(`${base(workspaceId)}/connection/install`, {});
}

/**
 * Ask the server for a GitHub App manifest to submit.
 *
 * The browser has to POST this to GitHub as a real form — GitHub shows the
 * person a confirmation screen listing exactly what is being created, and a
 * fetch would never let them see it.
 */
export function startRegistration(
  workspaceId: string,
  organisation: string,
): Promise<{ postUrl: string; manifest: string }> {
  return api.post(`${base(workspaceId)}/connection/register`, { organisation });
}

export function disconnectGitHub(workspaceId: string): Promise<{ disconnected: boolean }> {
  return api.delete(`${base(workspaceId)}/connection`);
}

export function addRepo(workspaceId: string, url: string, branch?: string): Promise<MappedRepo> {
  return api.post(`${base(workspaceId)}/repos`, branch === undefined ? { url } : { url, branch });
}

export function fetchBranches(workspaceId: string, repoId: string): Promise<{ branches: string[] }> {
  return api.get(`${base(workspaceId)}/repos/${encodeURIComponent(repoId)}/branches`);
}

/**
 * Branches on a repository that is not connected yet.
 *
 * Keyed by URL because there is no row id until it is connected — and the
 * whole point is to choose the branch before that, since connecting is what
 * queues the first build.
 */
export function fetchBranchesForUrl(workspaceId: string, url: string): Promise<{ branches: string[] }> {
  return api.get(`${base(workspaceId)}/repos/available/branches?url=${encodeURIComponent(url)}`);
}

/**
 * Which branch the map is built from.
 *
 * GitHub's default is often wrong — `main` can be an unused stub, or the
 * default was never set properly — and a map built from a dead branch is
 * worse than no map, because it looks authoritative and describes nothing
 * that ships.
 */
export function setRepoBranch(
  workspaceId: string,
  repoId: string,
  defaultBranch: string,
): Promise<MappedRepo> {
  return api.patch(`${base(workspaceId)}/repos/${encodeURIComponent(repoId)}`, { defaultBranch });
}

export function removeRepo(workspaceId: string, repoId: string): Promise<{ removed: boolean }> {
  return api.delete(`${base(workspaceId)}/repos/${encodeURIComponent(repoId)}`);
}

/**
 * Queued rather than awaited — a full build runs for minutes. The screen
 * polls the map afterwards rather than holding a request open.
 */
export function buildMap(
  workspaceId: string,
  full: boolean,
): Promise<{ status: string; version?: number | null; reason?: string }> {
  return api.post(`${base(workspaceId)}/build?full=${full ? 'true' : 'false'}`, {});
}

export function saveMap(
  workspaceId: string,
  content: SystemMapContent,
  reviewed: boolean,
): Promise<{ version: number; reviewedAt: string | null }> {
  return api.put(`${base(workspaceId)}`, { content, reviewed });
}
